/* ============================================================
   BEZET — образи: вибір складу, розмірів і додавання в кошик.

   Модель: за замовчуванням береться ВЕСЬ образ, клієнт лише знімає
   зайве. Раніше кожен рядок мав «+», який конкурував із кнопкою внизу —
   було незрозуміло, що головне.

   Розміри наперед НЕ проставляємо (крім речей з єдиною опцією): перший
   доступний розмір — майже завжди XS, і випадкове «Додати все» дало б
   комплект не того розміру. Замість цього працює автопідстановка:
   обрали L на куртці — L проставиться скрізь, де він є.

   Усі підписи приходять у data-атрибутах із секції.
   Плейсхолдери: [count], [total], [size].
   ============================================================ */

class LookCart extends HTMLElement {
  connectedCallback() {
    this.handle = this.dataset.lookHandle || '';
    this.picked = {}; // productId -> { id, label }
    this.added = {}; // productId -> назва розміру
    this.chosen = {}; // productId -> чи входить у замовлення

    this.items = Array.from(this.querySelectorAll('[data-look-item]'));
    this.addAllEl = this.querySelector('[data-look-add-all]');
    this.totalEl = this.querySelector('[data-look-total]');

    this.sellable.forEach((row) => {
      const id = row.dataset.productId;
      const box = row.querySelector('[data-look-pick]');
      this.chosen[id] = box ? box.checked : true;
      this.preselectSingleSize(row);
    });

    this.addEventListener('click', this.onClick.bind(this));
    this.addEventListener('change', this.onChange.bind(this));
    this.watchPrices();
    this.sync();
  }

  get labels() {
    return {
      choose: this.dataset.labelChoose || '',
      addAll: this.dataset.labelAddAll || '',
      addRest: this.dataset.labelAddRest || '',
      addSelected: this.dataset.labelAddSelected || '',
      nothing: this.dataset.labelNothing || '',
      allInCart: this.dataset.labelAllInCart || '',
      inCart: this.dataset.labelInCart || '',
      inCartAria: this.dataset.labelInCartAria || '',
    };
  }

  fill(template, values) {
    return Object.keys(values).reduce(
      (out, key) => out.split('[' + key + ']').join(values[key]),
      template
    );
  }

  /** Позиції, які взагалі можна купити (є хоч один доступний розмір). */
  get sellable() {
    return this.items.filter((el) => el.dataset.available === '1');
  }

  /** Ще не в кошику. */
  get pending() {
    return this.sellable.filter((el) => !this.added[el.dataset.productId]);
  }

  /** Те, що піде в кошик за натиском кнопки. */
  get active() {
    return this.pending.filter((el) => this.chosen[el.dataset.productId]);
  }

  /** Річ з єдиною опцією (Onesize) — вибирати нема з чого, ставимо самі. */
  preselectSingleSize(row) {
    const options = Array.from(row.querySelectorAll('[data-look-size]')).filter((b) => !b.disabled);
    if (options.length !== 1) return;
    const only = options[0];
    this.picked[row.dataset.productId] = {
      id: only.dataset.variantId,
      label: only.textContent.trim(),
    };
    this.markSizes(row, only.textContent.trim());
  }

  onChange(event) {
    const box = event.target.closest('[data-look-pick]');
    if (!box) return;
    const row = box.closest('[data-look-item]');
    this.chosen[row.dataset.productId] = box.checked;
    row.classList.toggle('lk-item--off', !box.checked);
    this.sync();
  }

  onClick(event) {
    const size = event.target.closest('[data-look-size]');
    if (size && !size.disabled) return this.chooseSize(size);

    if (this.addAllEl && event.target.closest('[data-look-add-all]') && !this.addAllEl.disabled) {
      return this.addItems(this.active, this.addAllEl);
    }
  }

  chooseSize(button) {
    const row = button.closest('[data-look-item]');
    const id = row.dataset.productId;
    const label = button.textContent.trim();
    const isSame = this.picked[id] && this.picked[id].label === label;

    this.picked[id] = isSame ? null : { id: button.dataset.variantId, label: label };
    this.markSizes(row, isSame ? null : label);

    /* Підставляємо той самий розмір там, де він існує й доступний:
       знімає половину кліків і майже завжди вгадує. Зняті позиції
       не чіпаємо — клієнт їх свідомо виключив. */
    if (!isSame) {
      this.sellable.forEach((other) => {
        const otherId = other.dataset.productId;
        if (otherId === id || this.picked[otherId] || this.added[otherId]) return;
        if (!this.chosen[otherId]) return;
        const match = Array.from(other.querySelectorAll('[data-look-size]')).find(
          (b) => !b.disabled && b.textContent.trim() === label
        );
        if (!match) return;
        this.picked[otherId] = { id: match.dataset.variantId, label: label };
        this.markSizes(other, label);
      });
    }

    this.sync();
  }

  markSizes(row, activeLabel) {
    row.querySelectorAll('[data-look-size]').forEach((b) => {
      b.setAttribute('aria-pressed', String(b.textContent.trim() === activeLabel));
    });
  }

  /* ---------- сума ----------
     Рахуємо не з data-price, а з того, що покупець реально бачить: у цьому
     магазині ціни на льоту конвертує апка валют, і власне форматування
     розійшлося б із рядками товарів. */

  parseMoney(text) {
    const cleaned = String(text).replace(/[^\d.,]/g, '');
    if (!cleaned) return 0;
    const dec = cleaned.match(/[.,](\d{2})$/);
    const whole = dec ? cleaned.slice(0, -3) : cleaned;
    return Number(whole.replace(/\D/g, '') || 0) * 100 + (dec ? Number(dec[1]) : 0);
  }

  /** Складаємо суму за зразком рядка товару: символ, позиція, роздільники. */
  formatLike(sample, cents) {
    const num = String(sample).match(/[\d.,]+/);
    if (!num) return String(Math.round(cents / 100));

    const decMatch = num[0].match(/([.,])(\d{2})$/);
    const decSep = decMatch ? decMatch[1] : null;
    const grouped = /\d[.,]\d{3}/.test(num[0]);

    let whole = String(Math.floor(cents / 100));
    if (grouped) whole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, decSep === ',' ? '.' : ',');

    const out = decSep ? whole + decSep + String(cents % 100).padStart(2, '0') : whole;
    return String(sample).replace(num[0], out);
  }

  priceText(row) {
    const el = row.querySelector('.lk-item__price');
    if (!el) return '';
    // Закреслена стара ціна не має потрапляти в суму
    const clone = el.cloneNode(true);
    clone.querySelectorAll('s').forEach((s) => s.remove());
    return clone.textContent.trim();
  }

  renderTotal() {
    if (!this.totalEl) return;

    /* Коли додавати нічого, «Разом 0,00» вводить в оману — сума більше
       ні до чого не належить. Ховаємо рядок цілком. */
    const box = this.totalEl.closest('dl') || this.totalEl;
    box.hidden = this.pending.length === 0;
    if (box.hidden) return;

    const sample = this.priceText(this.sellable[0] || this.items[0]);
    const cents = this.active.reduce(
      (sum, row) => sum + this.parseMoney(this.priceText(row)),
      0
    );
    this.totalEl.textContent = this.formatLike(sample, cents);
  }

  /* Апка валют переписує ціни вже після завантаження — перерахуємо суму,
     коли це станеться, інакше вона лишиться в старій валюті. */
  watchPrices() {
    const first = this.querySelector('.lk-item__price');
    if (!first || !('MutationObserver' in window)) return;
    this.priceObserver = new MutationObserver(() => this.renderTotal());
    this.priceObserver.observe(first, { childList: true, characterData: true, subtree: true });
  }

  disconnectedCallback() {
    if (this.priceObserver) this.priceObserver.disconnect();
  }

  async addItems(rows, trigger) {
    const lines = rows
      .map((row) => this.picked[row.dataset.productId])
      .filter(Boolean)
      .map((choice) => ({
        id: Number(choice.id),
        quantity: 1,
        properties: this.handle ? { _look: this.handle } : undefined,
      }));

    if (!lines.length) return;

    trigger.setAttribute('aria-disabled', 'true');
    trigger.classList.add('loading');

    /* Спершу drawer: у цій темі кошик саме висувний, а cart-notification
       присутній у DOM, але його секції на сторінці метаоб'єкта не рендеряться. */
    const cart =
      document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    const config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : { headers: {} };
    config.method = 'POST';
    config.headers['Content-Type'] = 'application/json';
    config.headers['X-Requested-With'] = 'XMLHttpRequest';

    const payload = { items: lines };
    if (cart && typeof cart.getSectionsToRender === 'function') {
      payload.sections = cart.getSectionsToRender().map((s) => s.id);
      payload.sections_url = window.location.pathname;
    }
    config.body = JSON.stringify(payload);

    try {
      const response = await fetch(window.routes.cart_add_url, config);
      const data = await response.json();

      if (data.status) {
        if (typeof publish === 'function') {
          publish(PUB_SUB_EVENTS.cartError, { source: 'look-cart', errors: data.errors, message: data.message });
        }
        return;
      }

      rows.forEach((row) => {
        const choice = this.picked[row.dataset.productId];
        if (choice) this.added[row.dataset.productId] = choice.label;
      });

      /* Спочатку власний стан. Раніше sync() стояв після оновлення кошика,
         і виняток у renderContents теми (він не перевіряє, чи існує цільовий
         вузол) зривав оновлення рядка. */
      this.sync();

      if (typeof publish === 'function') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'look-cart', cartData: data });
      }
      this.refreshCart(cart, data);
    } catch (error) {
      console.error(error);
    } finally {
      trigger.removeAttribute('aria-disabled');
      trigger.classList.remove('loading');
    }
  }

  /**
   * Оновлює розмітку кошика власноруч. renderContents теми падає, якщо
   * цільового вузла або секції немає на сторінці — тут кожен крок
   * перевіряється, і збій однієї секції не ламає решту.
   */
  refreshCart(cart, data) {
    if (!cart || !data || !data.sections || typeof cart.getSectionsToRender !== 'function') return;

    cart.getSectionsToRender().forEach((section) => {
      try {
        const target = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
        const html = data.sections[section.section || section.id];
        if (!target || !html) return;

        const parsed = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector(section.selector || '.shopify-section');
        if (parsed) target.innerHTML = parsed.innerHTML;
      } catch (error) {
        console.error(error);
      }
    });

    /* Показуємо кошик, щоб додавання було видимим одразу */
    if (typeof cart.toggleDrawerClasses === 'function' && !cart.isOpen) {
      cart.toggleDrawerClasses();
    }
  }

  sync() {
    const L = this.labels;

    this.sellable.forEach((row) => {
      const id = row.dataset.productId;
      if (!this.added[id]) return;

      row.classList.add('is-added');
      const box = row.querySelector('[data-look-pick]');
      if (box) {
        box.checked = true;
        box.disabled = true;
      }

      const sizes = row.querySelector('[data-look-sizes]');
      if (sizes) {
        /* Замість вимкнених чипів — рядок стану. Вимкнені чипи виглядають
           як розпродані розміри, а річ насправді вже в кошику. */
        const note = document.createElement('p');
        note.className = 'lk-item__state';
        note.textContent = this.fill(L.inCart, { size: this.added[id] });
        sizes.replaceWith(note);
      }
    });

    this.renderTotal();

    if (!this.addAllEl) return;

    const pending = this.pending;
    const active = this.active;
    const ready = active.filter((row) => this.picked[row.dataset.productId]);

    if (!pending.length) {
      this.addAllEl.disabled = true;
      this.addAllEl.textContent = L.allInCart;
    } else if (!active.length) {
      this.addAllEl.disabled = true;
      this.addAllEl.textContent = L.nothing;
    } else if (ready.length < active.length) {
      this.addAllEl.disabled = true;
      this.addAllEl.textContent = this.fill(L.choose, {
        count: ready.length,
        total: active.length,
      });
    } else {
      this.addAllEl.disabled = false;
      let template = L.addSelected;
      if (active.length === this.sellable.length) template = L.addAll;
      else if (active.length === pending.length) template = L.addRest;
      this.addAllEl.textContent = this.fill(template, { count: active.length });
    }
  }
}

customElements.define('look-cart', LookCart);
